import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, CheckboxField, Spinner } from "../../../components/ui";
import { ConfirmDialog } from "../../../components/ui/overlay/ConfirmDialog";
import { ApiError } from "../../../lib/api/client";
import { AppShell } from "../../../layouts/AppShell";
import { getQuestionnaire } from "../api/questionnaireAdminApi";
import {
  getCurrentQuestionnaireSubmission,
  submitQuestionnaire,
  updateCurrentQuestionnaireSubmission,
} from "../api/questionnaireResponseApi";
import { FixedQuestionnaireForm } from "../components/FixedQuestionnaire";
import type {
  Questionnaire,
  SubmissionAnswerInput,
  SubmissionDetail,
} from "../schemas/questionnaireSchemas";

type BuildAnswersResult =
  | { ok: true; answers: SubmissionAnswerInput[] }
  | { ok: false; error: string };

function questionLabel(question: Questionnaire["questions"][number]) {
  return `第 ${question.position} 題「${question.prompt}」`;
}

export function buildAnswers(
  values: Record<string, string | number>,
  questions: Questionnaire["questions"],
): BuildAnswersResult {
  const answers: SubmissionAnswerInput[] = [];
  for (const question of questions) {
    const value = values[question.key];
    if (value === undefined || value === "") {
      if (question.required) {
        return {
          ok: false,
          error: `${questionLabel(question)}尚未填寫。`,
        };
      }
      continue;
    }
    if (question.type === "TEXT") {
      const textValue = String(value).trim();
      if (!textValue && question.required) {
        return {
          ok: false,
          error: `${questionLabel(question)}尚未填寫。`,
        };
      }
      if (textValue) answers.push({ questionKey: question.key, textValue });
      continue;
    }
    if (question.type === "RATING_SCALE") {
      if (typeof value !== "number") {
        return {
          ok: false,
          error: `${questionLabel(question)}尚未完成評分。`,
        };
      }
      answers.push({ questionKey: question.key, ratingValue: value });
      continue;
    }
    const selectedValue = String(value);
    if (selectedValue === "OTHER") {
      const otherText = String(values[`${question.key}:other`] ?? "").trim();
      if (!otherText) {
        return {
          ok: false,
          error: `${questionLabel(question)}已選擇「其他」，請補充說明。`,
        };
      }
      answers.push({ questionKey: question.key, selectedValue, otherText });
      continue;
    }
    answers.push({ questionKey: question.key, selectedValue });
  }
  return { ok: true, answers };
}

export function submissionValuesFromDetail(
  submission: SubmissionDetail,
): Record<string, string | number> {
  return submission.answers.reduce<Record<string, string | number>>((accumulator, answer) => {
    if (answer.textValue) {
      accumulator[answer.questionKey] = answer.textValue;
    }
    if (answer.selectedValue) {
      accumulator[answer.questionKey] = answer.selectedValue;
      if (answer.selectedValue === "OTHER" && answer.otherText) {
        accumulator[`${answer.questionKey}:other`] = answer.otherText;
      }
    }
    if (typeof answer.ratingValue === "number") {
      accumulator[answer.questionKey] = answer.ratingValue;
    }
    return accumulator;
  }, {});
}

export function QuestionnaireResponsePage() {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ["questionnaire"], queryFn: getQuestionnaire });
  const currentSubmissionQuery = useQuery({
    queryKey: ["questionnaire", "current-submission", query.data?.versionId],
    queryFn: getCurrentQuestionnaireSubmission,
    enabled: query.data?.availability.status === "OPEN",
    retry: false,
  });
  const [values, setValues] = useState<Record<string, string | number>>({});
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string>();
  const [editingSubmissionId, setEditingSubmissionId] = useState<string>();
  const [resumePromptOpen, setResumePromptOpen] = useState(false);
  const [resumeResolved, setResumeResolved] = useState(false);
  const [editingNotice, setEditingNotice] = useState<string>();

  useEffect(() => {
    if (query.data?.availability.status !== "OPEN") return;
    if (!currentSubmissionQuery.isSuccess || resumeResolved) return;
    setResumeResolved(true);
    if (currentSubmissionQuery.data) {
      setResumePromptOpen(true);
    }
  }, [
    currentSubmissionQuery.data,
    currentSubmissionQuery.isSuccess,
    query.data?.availability.status,
    resumeResolved,
  ]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    if (!query.data) return;
    const result = buildAnswers(values, query.data.questions);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (!accepted) {
      setError("請先確認具名填答告知。");
      return;
    }
    try {
      if (editingSubmissionId) {
        await updateCurrentQuestionnaireSubmission(result.answers, true);
      } else {
        await submitQuestionnaire(result.answers, true);
      }
      navigate("/", { replace: true });
    } catch (reason) {
      if (reason instanceof ApiError) {
        if (reason.problem.code === "QUESTIONNAIRE_ALREADY_SUBMITTED") {
          setError("您已完成此版本問卷；若要修改，請重新進入問卷並載入既有填答。");
          return;
        }
        if (reason.problem.code === "QUESTIONNAIRE_SUBMISSION_NOT_FOUND") {
          setEditingSubmissionId(undefined);
          setError("找不到您目前版本的既有填答，請重新整理後再試。");
          void currentSubmissionQuery.refetch();
          return;
        }
        if (reason.problem.code === "QUESTIONNAIRE_CLOSED") {
          setError("問卷目前未開放或已截止。");
          return;
        }
      }
      setError("問卷送出失敗，系統未建立部分填答，請稍後再試。");
    }
  };

  const loadExistingSubmission = () => {
    if (!currentSubmissionQuery.data) return;
    setValues(submissionValuesFromDetail(currentSubmissionQuery.data));
    setAccepted(true);
    setEditingSubmissionId(currentSubmissionQuery.data.id);
    setEditingNotice("已載入您先前提交的內容，修改後重新送出即可覆寫目前版本。");
    setError(undefined);
    setResumePromptOpen(false);
  };

  const returnToDashboard = () => {
    setResumePromptOpen(false);
    navigate("/", { replace: true });
  };

  const waitingForCurrentSubmission =
    query.data?.availability.status === "OPEN" && currentSubmissionQuery.isLoading && !resumeResolved;

  return (
    <AppShell title="數產署效能評估工具使用問卷" breadcrumb={["固定問卷"]}>
      {(query.isLoading || waitingForCurrentSubmission) && <Spinner label="載入問卷" />}
      {query.data?.availability.status === "CLOSED" && (
        <Alert variant="warning">問卷目前未開放或已截止。</Alert>
      )}
      {currentSubmissionQuery.isError && query.data?.availability.status === "OPEN" && (
        <Alert variant="danger">無法檢查既有填答紀錄，請重新整理後再試。</Alert>
      )}
      {query.data && query.data.availability.status === "OPEN" && !waitingForCurrentSubmission && (
        <form onSubmit={(event) => void submit(event)} noValidate>
          <Alert variant="info">{query.data.namedResponseNotice}</Alert>
          {editingNotice && <Alert variant="success">{editingNotice}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          <Card>
            <p className="text-muted">目前填寫版本：V{query.data.versionNumber}</p>
            <FixedQuestionnaireForm
              questionnaire={query.data}
              values={values}
              onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
            />
            <CheckboxField
              label="我已閱讀並同意上述具名填答告知"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
            />
            <Button type="submit">{editingSubmissionId ? "更新問卷" : "送出問卷"}</Button>
          </Card>
        </form>
      )}
      <ConfirmDialog
        open={resumePromptOpen}
        title="已完成過目前版本問卷"
        description="是否載入您先前提交的內容進行修改？若不同意，系統會返回總覽頁。"
        confirmLabel="載入舊問卷紀錄"
        cancelLabel="返回總覽頁"
        onConfirm={loadExistingSubmission}
        onClose={returnToDashboard}
      />
    </AppShell>
  );
}
